/**
 * HealthBar (Nano-Metadata Table)
 *
 * ✅ ENTERPRISE V2: Strictly No Speculation
 *
 * RULES:
 *   1. Health is NULL until processingProgress === 100
 *   2. Show "Pending" state with no bar until complete
 *   3. Never show speculative percentages (60%, 85% before processing)
 */

import React from 'react';

import { Loader2, CheckCircle, AlertTriangle, XCircle, Clock } from 'lucide-react';
import '@/components/smart-notebook/SmartTable/SmartTable.css';

export default function HealthBar({ row }) {
  // ✅ FINAL UX CONTRACT: Do not show IDX/TXT to users. Render the server-side Trust badge only.
  const trust = row?.user_display?.trust || null;
  const isPending = row?.quick_health?.is_pending === true;
  const isLoading = row?.isLoading === true;
  const hasError = row?.hasError === true || row?.status === 'error';

  // ═══════════════════════════════════════════════════════════════════════════
  // ERROR STATE
  // ═══════════════════════════════════════════════════════════════════════════
  if (hasError) {
    return (
      <div className="health-bar health-bar-error">
        <div className="health-bar-track">
          <div className="health-bar-fill health-critical" style={{ width: '100%' }} />
        </div>
        <span className="health-text health-error">
          <XCircle size={12} className="health-icon" />
          Failed
        </span>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PENDING STATE (isLoading OR healthScore is null)
  // Shows "Pending" with no speculative bar
  // ═══════════════════════════════════════════════════════════════════════════
  if (isLoading || isPending || !trust) {
    return (
      <div className="health-bar health-bar-pending">
        <div className="health-bar-track health-bar-track-pending">
          <div className="health-bar-fill health-pending-anim" />
        </div>
        <span className="health-text health-pending">
          <Clock size={12} className="health-icon health-icon-spin" />
          Pending
        </span>
      </div>
    );
  }

  const state = String(trust.state || '').toLowerCase();
  const colorClass =
    state === 'trusted'
      ? 'health-excellent'
      : state === 'readable'
        ? 'health-good'
        : state === 'low_quality'
          ? 'health-critical'
          : 'health-pending';
  const Icon =
    state === 'low_quality'
      ? XCircle
      : state === 'processing' || state === 'reading'
        ? Clock
        : CheckCircle;
  const tooltip = trust.tooltip || '';

  return (
    <div className="health-bar" title={tooltip}>
      <div className="health-bar-track">
        <div className={`health-bar-fill ${colorClass}`} style={{ width: '100%' }} />
      </div>
      <span className={`health-text ${colorClass}`}>
        <Icon size={12} className="health-icon" />
        {trust.emoji} {trust.badge}
      </span>
    </div>
  );
}
